import AbstractViewer, {AbstractViewerProperties, AbstractViewerState, VIEWER_PADDING} from "../AbstractViewer";
import React, {createRef, ReactNode, RefObject} from "react";
import DomHelper from "../../../shared/helper/DomHelper";
import PageViewMode from "../settings/PageViewMode";
import MouseMode from "../settings/MouseMode";
import PortalDocument from "../../document/PortalDocument";
import DocumentSettings, {DocumentSettingsViewport} from "../../document/DocumentSettings";
import {pdfjs} from "react-pdf";
import {ViewerMode} from "../ViewerMode";
import ZoomMode from "../settings/ZoomMode";
import PdfPage, {PdfPageDefinition} from "./PdfPage";
import MouseFunction from "../settings/MouseFunction";
import LengthUnit from "../../../shared/units/LengthUnit";
import {PortalGlobals} from "../../Portal";
import NumberHelper from "../../../shared/helper/NumberHelper";
import MessageBox from "../../../shared/components/MessageBox";
import i18next from "i18next";
import Exception from "../../../shared/exception/Exception";
import {Password} from "primereact/password";
import {Button} from "primereact/button";
import FunctionHelper from "../../../shared/helper/FunctionHelper";
import UnitHelper from "../../../shared/helper/UnitHelper";
import Ruler, {RULER_PADDING, RulerDirection} from "./Ruler";
import {InputNumber} from "primereact/inputnumber";
import {Menu} from "primereact/menu";
import Observer from "../../../shared/class/Observer";
import DestinationEvent from "../DestinationEvent";
import LocationEvent from "../LocationEvent";
import {TextSearchGlobals} from "../../document/components/pdf/TextSearch";
import {
	Attachment,
	CertificateEntry,
	DocMdp,
	DocumentPassword,
	DocumentRecipient,
	FileDataSource,
	MetadataFormsFormat,
	MetadataInformation,
	MetadataRectangle,
	PagePages,
	PdfPassword,
	PdfPasswordInterface,
	Point,
	PrivateKeyFileData,
	PrivateKeyFileDataInterface,
	Rectangle,
	UserCertificates
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import ErrorCode from "../../../shared/exception/ErrorCode";
import CertificateInput, {CertificateInputType} from "../../../shared/components/CertificateInput";
import {MessageType} from "../../../shared/log/MessageInterface";
import MenuButton from "../../../shared/menu/MenuButton";
import Immutable from "../../../shared/immutable/Immutable";
import Translate from "../../../shared/translation/Translate";
import {
	AreaLayerGlobals,
	PortalOperationLineAnnotation,
	PortalOperationPoint,
	PortalOperationRectangle
} from "./AreaLayerInterface";
import {DebugGlobals} from "../../dialog/components/settings/DebugDialog";
import UserSettings from "../../session/UserSettings";
import StorageItem from "../../../shared/StorageItem";
import {TextLayerGlobals} from "./TextLayer";
import {FormLayerGlobals} from "./form/FormLayerInterface";
import {SidebarFunctionGlobals} from "../../document/components/pdf/SidebarFunctions";

export const PAGE_PADDING: number = 30;

enum CertificateFields {
	CERTIFICATE = "certificate",
	PRIVATE_KEY = "private_key",
}

/**
 * this interface is used for dialog previews, so the zoomValue can be dynamic
 */
export interface PreviewState {
	renderPreview: boolean; // this is used to delay the preview rendering until the dialog is shown
	previewZoomValue: number;
}

export interface PdfViewerProperties extends AbstractViewerProperties, PortalGlobals, AreaLayerGlobals,
	TextSearchGlobals, DebugGlobals, TextLayerGlobals, FormLayerGlobals, SidebarFunctionGlobals {
	continuous: boolean;
	pagePadding: boolean;
	pageCover: boolean;
	fullscreen: boolean;
	currentPage: number;
	zoomValue: number;
	zoomMode: ZoomMode;
	pageMode: PageViewMode;
	mouseFunction: MouseFunction;
	destinationObserver?: Observer<DestinationEvent>;
	locationObserver?: Observer<LocationEvent>;
	updatePreviewState?: <K extends PreviewState>(state: K, callback?: () => void) => void;
}

export interface PdfViewerState extends AbstractViewerState {
	loading: boolean;
	showCoordinates: boolean;
	showFullscreenOptions: boolean;
	scrollOffset: number;
	pagesFrom: number;
	pagesTo: number;
	viewerHeight: number;
	computedZoom: number;
	coordinateX: number;
	coordinateY: number;
	scrollTop: number;
	scrollLeft: number;
	scrollWidth: number;
	scrollHeight: number;
	openPassword: string;
	privateKey: string;
	privateKeyPassword: string;
	currentZoomValue: number;
	zoomValue: number;
	error?: Exception;
	exception?: Exception;
	certificate?: CertificateEntry;
	fieldErrors: Map<CertificateFields, string>;
	interactivePage?: number;
}

export class PdfViewerDefinition extends AbstractViewer<PdfViewerProperties, PdfViewerState> {
	private loaderElement: RefObject<HTMLDivElement>;
	private resizeObserver: ResizeObserver;
	private intersectionObserver: IntersectionObserver;
	private pageVisibility: Map<number, number>;
	private stopPageScroll: boolean;

	public static defaults(): PdfViewerProperties {
		return {
			continuous: false,
			currentPage: 1,
			fullscreen: false,
			mouseFunction: MouseFunction.NONE,
			mouseMode: MouseMode.NONE,
			pageCover: false,
			pageMode: PageViewMode.SINGLE,
			pagePadding: false,
			viewerMode: ViewerMode.DEFAULT,
			zoomMode: ZoomMode.PAGE,
			zoomValue: 100
		} as PdfViewerProperties;
	}

	/**
	 * Constructs a {@link PdfViewer} component
	 */
	constructor(props: Readonly<PdfViewerProperties>) {
		super(props);

		this.state = {
			...this.state,
			loading: true,
			showCoordinates: false,
			showFullscreenOptions: false,
			scrollOffset: 0,
			pagesFrom: 1,
			pagesTo: 1,
			viewerHeight: 0,
			computedZoom: 100,
			openPassword: "",
			coordinateX: 0,
			coordinateY: 0,
			scrollTop: 0,
			scrollLeft: 0,
			scrollWidth: 0,
			scrollHeight: 0,
			currentZoomValue: 100,
			zoomValue: 100,
			privateKey: "",
			privateKeyPassword: "",
			fieldErrors: new Map<CertificateFields, string>()
		}

		this.updateZoom = this.updateZoom.bind(this);
		this.handleResize = this.handleResize.bind(this);
		this.computeViewport = this.computeViewport.bind(this);
		this.handleScroll = this.handleScroll.bind(this);
		this.handlePassword = this.handlePassword.bind(this);
		this.handleCertificate = this.handleCertificate.bind(this);
		this.handleIntersection = this.handleIntersection.bind(this);
		this.handleKeydown = this.handleKeydown.bind(this);
		this.handleContextmenu = this.handleContextmenu.bind(this);
		this.updateCoordinates = this.updateCoordinates.bind(this);
		this.navigateToLocation = this.navigateToLocation.bind(this);
		this.navigateToDestination = this.navigateToDestination.bind(this);
		this.setScrollPosition = this.setScrollPosition.bind(this);
		this.handleInteractivity = this.handleInteractivity.bind(this);
		this.throttledSetActivePage = FunctionHelper.throttle(this.throttledSetActivePage.bind(this));
		this.throttledUpdateZoomValue = FunctionHelper.throttle(this.throttledUpdateZoomValue.bind(this));
		this.throttledZoom = FunctionHelper.throttle(this.throttledZoom.bind(this));
		this.throttledScrollChangePage = FunctionHelper.throttle(this.throttledScrollChangePage.bind(this), 100);

		this.stopPageScroll = false;
		this.pageVisibility = new Map<number, number>();
		this.loaderElement = createRef<HTMLDivElement>();
		this.resizeObserver = new ResizeObserver(this.handleResize);

		let threshold: Array<number> = [];
		for (let i: number = 0; i <= 1; i += 0.001) {
			threshold.push(i);
		}

		this.intersectionObserver = new IntersectionObserver(this.handleIntersection, {threshold: threshold});
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		window.addEventListener('contextmenu', this.handleContextmenu);
		window.addEventListener('keydown', this.handleKeydown);

		if (FunctionHelper.exists(this.props.locationObserver)) {
			this.props.locationObserver!.subscribe(this.navigateToLocation);
		}

		if (FunctionHelper.exists(this.props.destinationObserver)) {
			this.props.destinationObserver!.subscribe(this.navigateToDestination);
		}

		if (FunctionHelper.exists(this.loaderElement.current)) {
			// Add active class after loader is mounted so the fade animation is applied
			DomHelper.addClass(this.loaderElement.current, "active");
		}


		if (FunctionHelper.exists(this.viewerElement.current)) {
			this.setState({
				scrollWidth: this.viewerElement.current!.scrollWidth,
				scrollHeight: this.viewerElement.current!.scrollHeight
			});
		}

		try {
			await super.componentDidMount();
		} catch (e: any) {
			let error: Exception = e;

			this.setDocumentErrors();

			this.setState({
				loading: false,
				error: error
			});
			return;
		}

		this.setState({
			loading: false,
			error: undefined
		});
	}

	/**
	 * This is called when the component is unmounted
	 */
	public async componentWillUnmount(): Promise<void> {
		window.removeEventListener('contextmenu', this.handleContextmenu);
		window.removeEventListener('keydown', this.handleKeydown);

		if (FunctionHelper.exists(this.props.locationObserver)) {
			this.props.locationObserver!.unsubscribe(this.navigateToLocation);
		}

		if (FunctionHelper.exists(this.props.destinationObserver)) {
			this.props.destinationObserver!.unsubscribe(this.navigateToDestination);
		}

		this.setState({
			loading: true,
			error: undefined
		});

		if (FunctionHelper.exists(this.intersectionObserver)) {
			this.intersectionObserver.disconnect();
		}

		await super.componentWillUnmount();
		this.resizeObserver.disconnect();
	}

	private async updateDocument(): Promise<void> {
		this.setState({
			loading: true,
			error: undefined
		});

		try {
			await super.componentWillUnmount();
			await super.componentDidMount();
		} catch (e: any) {
			let error: Exception = e;

			this.setDocumentErrors();
			this.setState({
				loading: false,
				error: error
			});
			return;
		}

		this.setState({
			loading: false,
			error: undefined
		});
	}

	/**
	 * This is called when the components props or state is updated
	 */
	public async componentDidUpdate(
		prevProps: Readonly<PdfViewerProperties>, prevState: Readonly<PdfViewerState>
	): Promise<void> {
		if (this.state.loading) {
			return;
		}

		if (this.props.portalDocument.get().id !== prevProps.portalDocument?.get().id ||
			this.props.portalDocument.get().updated !== prevProps.portalDocument?.get().updated ||
			this.props.attachmentKey !== prevProps.attachmentKey
		) {
			this.setState({
				certificate: undefined,
				privateKey: "",
				privateKeyPassword: "",
				openPassword: ""
			});

			await this.updateDocument();
			return;
		}

		if (this.state.zoomValue !== this.props.zoomValue) {
			this.throttledUpdateZoomValue();
		}

		if (this.state.loading !== prevState.loading && this.viewerElement.current !== null &&
			!FunctionHelper.exists(this.state.error)) {
			this.resizeObserver.observe(this.viewerElement.current);
		}

		if (this.props.currentPage !== prevProps.currentPage || this.props.continuous !== prevProps.continuous ||
			this.props.pageMode !== prevProps.pageMode || this.props.zoomMode !== prevProps.zoomMode) {
			if (!this.stopPageScroll && this.props.continuous) {
				await this.setScrollPosition(0, 0, this.props.currentPage);
			}

			this.stopPageScroll = false;
		}

		if ((
				this.state.loading !== prevState.loading || this.props.zoomMode !== prevProps.zoomMode ||
				this.props.zoomValue !== prevProps.zoomValue || this.props.continuous !== prevProps.continuous ||
				(this.props.currentPage !== prevProps.currentPage && !this.props.continuous) ||
				this.props.pageMode !== prevProps.pageMode || this.props.portalResolution !== prevProps.portalResolution
			) &&
			this.viewerElement.current !== null && !FunctionHelper.exists(this.state.error)) {
			await this.updateZoom();
			return;
		}

		let changeViewport: Array<boolean> = [
			this.props.pageCover !== prevProps.pageCover
		];

		if (changeViewport.includes(true) && !FunctionHelper.exists(this.state.error)) {
			await this.computeViewport();
			await this.setScrollPosition(0, 0, this.props.currentPage);
		}
	}

	/**
	 * update mouse coordinates on a pdf page
	 */
	private updateCoordinates(event: React.MouseEvent): void {
		if (this.props.viewerMode !== ViewerMode.DEFAULT && this.props.viewerMode !== ViewerMode.PORTFOLIO) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument.get().id!
		);
		let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();

		let eventTarget: HTMLElement = event.target as HTMLElement;
		let pageRect: DOMRect = eventTarget.getBoundingClientRect();
		let zoomScale = viewerSettings.activeViewer.zoomValue / 100;
		let offsetX: number = (event.clientX - pageRect.left) / zoomScale;
		let offsetY: number = (event.clientY - pageRect.top) / zoomScale;

		this.setState({
			coordinateX: offsetX,
			coordinateY: offsetY
		});
	}

	private async updateZoom(): Promise<void> {
		if (!this.mounted) {
			return;
		}

		try {
			let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
				this.props.portalDocument.get().id!
			);
			let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();

			if (!portalDocument.container.loaded) {
				return;
			}

			let viewerData: pdfjs.PDFDocumentProxy;
			if (this.props.viewerMode === ViewerMode.ATTACHMENT ||
				this.props.viewerMode === ViewerMode.PORTFOLIO ||
				this.props.viewerMode === ViewerMode.PORTFOLIO_PREVIEW ||
				this.props.viewerMode === ViewerMode.PORTFOLIO_PAGE) {
				let attachment: Attachment = portalDocument.getAttachment(this.props.attachmentKey!)!;
				viewerData = await portalDocument.loadViewerData(attachment);
			} else {
				viewerData = await portalDocument.loadViewerData();
			}

			if (!this.mounted) {
				return;
			}

			let viewports: Array<pdfjs.PageViewport> = [];
			for (let pageNumber = 1; pageNumber <= viewerData.numPages; pageNumber++) {
				let page: pdfjs.PDFPageProxy = await viewerData.getPage(pageNumber);
				let viewport: pdfjs.PageViewport = page.getViewport({scale: 1});
				viewports.push(viewport);
			}

			if (this.viewerElement.current === null) {
				return;
			}

			let viewerRect: DOMRect = this.viewerElement.current.getBoundingClientRect();

			let boundaryWidth: number = viewerRect.width;
			boundaryWidth -=
				this.props.viewerMode === ViewerMode.DEFAULT || this.props.viewerMode === ViewerMode.PORTFOLIO ?
					VIEWER_PADDING + RULER_PADDING : 0;
			boundaryWidth -=
				this.props.viewerMode === ViewerMode.DEFAULT || this.props.viewerMode === ViewerMode.PORTFOLIO ?
					VIEWER_PADDING + DomHelper.SCROLLBAR_SIZE : 0;
			boundaryWidth = Math.max(boundaryWidth, 0);

			let boundaryHeight: number = viewerRect.height;
			boundaryHeight -=
				this.props.viewerMode === ViewerMode.DEFAULT || this.props.viewerMode === ViewerMode.PORTFOLIO ?
					VIEWER_PADDING + RULER_PADDING : 0;
			boundaryHeight -=
				this.props.viewerMode === ViewerMode.DEFAULT || this.props.viewerMode === ViewerMode.PORTFOLIO ?
					VIEWER_PADDING + DomHelper.SCROLLBAR_SIZE : 0;
			boundaryHeight = Math.max(boundaryHeight, 0);

			let computedViewportHeight: number = 0;
			let computedViewportWidth: number = 0;

			for (let [index, viewport] of viewports.entries()) {
				let pageNumber: number = index + 1;
				let viewportWidth: number = UnitHelper.unitToPx(
					viewport.width, LengthUnit.POINTS, this.props.portalResolution
				);
				let viewportHeight: number = UnitHelper.unitToPx(
					viewport.height, LengthUnit.POINTS, this.props.portalResolution
				);

				if (this.props.pageMode === PageViewMode.SINGLE) {
					if (this.props.continuous) {
						if (viewportWidth > computedViewportWidth) {
							computedViewportWidth = viewportWidth;
						}

						if (viewportHeight > computedViewportHeight) {
							computedViewportHeight = viewportHeight;
						}
					} else if (pageNumber === this.props.currentPage) {
						computedViewportWidth = viewportWidth;
						computedViewportHeight = viewportHeight;
					}
				} else if (this.props.pageMode === PageViewMode.DUAL) {
					if (this.props.continuous) {
						if (pageNumber % 2 === 1) {
							let additionalViewport: DocumentSettingsViewport | undefined =
								viewports[index + (pageNumber % 2 === 1 ? 1 : -1)];

							let tempWidth: number = viewportWidth;
							tempWidth += UnitHelper.unitToPx(
								FunctionHelper.applyValue(additionalViewport?.width, 0), LengthUnit.POINTS,
								this.props.portalResolution
							);
							if (tempWidth > computedViewportWidth) {
								computedViewportWidth = tempWidth;
							}
						}

						if (viewportHeight > computedViewportHeight) {
							computedViewportHeight = viewportHeight;
						}
					} else if (pageNumber === this.props.currentPage) {
						let additionalViewport: DocumentSettingsViewport | undefined =
							viewports[index + (pageNumber % 2 === 1 ? 1 : -1)];
						let additionalViewportHeight: number = UnitHelper.unitToPx(
							FunctionHelper.applyValue(additionalViewport.height, 0), LengthUnit.POINTS,
							this.props.portalResolution
						);
						let additionalViewportWidth: number = UnitHelper.unitToPx(
							FunctionHelper.applyValue(additionalViewport.width, 0), LengthUnit.POINTS,
							this.props.portalResolution
						);

						if (FunctionHelper.exists(additionalViewport)) {
							computedViewportWidth = viewportWidth + additionalViewportWidth;
							computedViewportHeight = viewportHeight > additionalViewportHeight ?
								viewportHeight : additionalViewportHeight;
						} else {
							computedViewportWidth = viewportWidth;
							computedViewportHeight = viewportHeight;
						}
					}
				}
			}

			let computedScaleHeight: number = boundaryHeight / computedViewportHeight;
			let computedScaleWidth: number = boundaryWidth / computedViewportWidth;
			let scale: number = 1;

			switch (this.props.zoomMode) {
				case ZoomMode.CUSTOM:
					scale = this.props.zoomValue / 100;
					break;
				case ZoomMode.HEIGHT:
					scale = computedScaleHeight;
					break;
				case ZoomMode.WIDTH:
					scale = computedScaleWidth;
					break;
				case ZoomMode.PAGE:
					scale = computedScaleHeight >= computedScaleWidth ? computedScaleWidth : computedScaleHeight;
					break;
				case ZoomMode.ORIGINAL:
					scale = 1;
					break;
			}

			if (this.props.viewerMode === ViewerMode.DEFAULT || this.props.viewerMode === ViewerMode.PORTFOLIO) {
				scale = NumberHelper.parseInt(scale * 100, {min: 10, max: 400}) / 100;
			}

			let zoomValue: number = scale * 100;

			this.setState({
				computedZoom: zoomValue
			});

			await this.computeViewport();
			await this.setScrollPosition(0, 0, this.props.currentPage);

			if ((this.props.viewerMode === ViewerMode.DEFAULT || this.props.viewerMode === ViewerMode.PORTFOLIO) &&
				this.props.zoomMode !== ZoomMode.CUSTOM && zoomValue !== this.props.zoomValue) {
				viewerSettings.activeViewer.zoomValue = zoomValue;
				this.props.context.documentManager.updateGlobalDocumentList();
			} else if (FunctionHelper.exists(this.props.updatePreviewState)) {
				this.props.updatePreviewState!({
					renderPreview: true,
					previewZoomValue: zoomValue
				});
			}
		} catch (e: any) {
			// can be ignored here
		}
	}

	private async computePageOffsetX(targetPage: number): Promise<number> {
		let scrollOffset: number = 0;

		try {
			let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
				this.props.portalDocument.get().id!
			);

			let viewerData: pdfjs.PDFDocumentProxy;
			if (this.props.viewerMode === ViewerMode.ATTACHMENT ||
				this.props.viewerMode === ViewerMode.PORTFOLIO ||
				this.props.viewerMode === ViewerMode.PORTFOLIO_PREVIEW ||
				this.props.viewerMode === ViewerMode.PORTFOLIO_PAGE) {
				let attachment: Attachment = portalDocument.getAttachment(this.props.attachmentKey!)!;
				viewerData = await portalDocument.loadViewerData(attachment);
			} else {
				viewerData = await portalDocument.loadViewerData();
			}

			if (!this.mounted) {
				return scrollOffset;
			}

			let viewports: Map<number, pdfjs.PageViewport> = new Map<number, pdfjs.PageViewport>();
			for (let pageNumber = 1; pageNumber <= viewerData.numPages; pageNumber++) {
				let page: pdfjs.PDFPageProxy = await viewerData.getPage(pageNumber);
				let viewport: pdfjs.PageViewport = page.getViewport({scale: this.state.computedZoom / 100});
				viewports.set(pageNumber, viewport);
			}

			let viewport: pdfjs.PageViewport | undefined = viewports.get(targetPage);
			let previousViewport: pdfjs.PageViewport | undefined = viewports.get(targetPage - 1);

			if (!FunctionHelper.exists(viewport) || !FunctionHelper.exists(previousViewport)) {
				return scrollOffset;
			}

			let previousViewportWidth: number = UnitHelper.unitToPx(
				FunctionHelper.applyValue(previousViewport?.width, 0), LengthUnit.POINTS,
				this.props.portalResolution
			);

			if (this.props.pageMode === PageViewMode.DUAL) {
				if ((this.props.pageCover && targetPage % 2 === 1) || (!this.props.pageCover && targetPage % 2 === 0)) {
					scrollOffset += previousViewportWidth + PAGE_PADDING;
				}
			}
		} catch (e: any) {
			// can be ignored here
		}

		return scrollOffset;
	}

	private async computePageOffsetY(targetPage: number): Promise<number> {
		let scrollOffset: number = 0;

		try {
			let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
				this.props.portalDocument.get().id!
			);

			let viewerData: pdfjs.PDFDocumentProxy;
			if (this.props.viewerMode === ViewerMode.ATTACHMENT ||
				this.props.viewerMode === ViewerMode.PORTFOLIO ||
				this.props.viewerMode === ViewerMode.PORTFOLIO_PREVIEW ||
				this.props.viewerMode === ViewerMode.PORTFOLIO_PAGE) {
				let attachment: Attachment = portalDocument.getAttachment(this.props.attachmentKey!)!;
				viewerData = await portalDocument.loadViewerData(attachment);
			} else {
				viewerData = await portalDocument.loadViewerData();
			}

			if (!this.mounted) {
				return scrollOffset;
			}

			let viewports: Map<number, pdfjs.PageViewport> = new Map<number, pdfjs.PageViewport>();
			for (let pageNumber = 1; pageNumber <= viewerData.numPages; pageNumber++) {
				let page: pdfjs.PDFPageProxy = await viewerData.getPage(pageNumber);
				let viewport: pdfjs.PageViewport = page.getViewport({scale: this.state.computedZoom / 100});
				viewports.set(pageNumber, viewport);
			}

			let itemsPerRow: number = this.props.pageMode === PageViewMode.SINGLE ? 1 : 2;

			if (this.props.continuous) {
				for (let [pageNumber, viewport] of viewports.entries()) {
					if (pageNumber === targetPage) {
						break;
					}

					let viewportHeight: number = UnitHelper.unitToPx(
						FunctionHelper.applyValue(viewport?.height, 0), LengthUnit.POINTS,
						this.props.portalResolution
					);
					let itemHeight: number = viewportHeight + PAGE_PADDING;
					let previousViewport: pdfjs.PageViewport | undefined = viewports.get(pageNumber - 1);
					let previousViewportHeight: number = UnitHelper.unitToPx(
						FunctionHelper.applyValue(previousViewport?.height, 0), LengthUnit.POINTS,
						this.props.portalResolution
					);

					if (this.props.pageMode === PageViewMode.SINGLE) {
						scrollOffset += itemHeight;
					} else {
						if (this.props.pageCover) {
							if (pageNumber === 1) {
								scrollOffset += itemHeight;
							} else if (pageNumber % itemsPerRow === 1) {
								if (FunctionHelper.exists(previousViewport) &&
									previousViewportHeight > viewportHeight) {
									let previousItemHeight: number = previousViewportHeight + PAGE_PADDING;
									scrollOffset += previousItemHeight;
								} else {
									scrollOffset += itemHeight;
								}
							}
						} else if (pageNumber % itemsPerRow === 0) {
							if (FunctionHelper.exists(previousViewport) && previousViewportHeight > viewportHeight) {
								let previousItemHeight: number = previousViewportHeight + PAGE_PADDING;
								scrollOffset += previousItemHeight;
							} else {
								scrollOffset += itemHeight;
							}
						}
					}
				}
			}
		} catch (e: any) {
			// can be ignored here
		}

		return scrollOffset;
	}

	private async computeViewport(): Promise<void> {
		if (this.viewerElement.current === null) {
			return;
		}

		try {
			let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
				this.props.portalDocument.get().id!
			);

			let viewerData: pdfjs.PDFDocumentProxy;
			if (this.props.viewerMode === ViewerMode.ATTACHMENT ||
				this.props.viewerMode === ViewerMode.PORTFOLIO ||
				this.props.viewerMode === ViewerMode.PORTFOLIO_PREVIEW ||
				this.props.viewerMode === ViewerMode.PORTFOLIO_PAGE) {
				let attachment: Attachment = portalDocument.getAttachment(this.props.attachmentKey!)!;
				viewerData = await portalDocument.loadViewerData(attachment);
			} else {
				viewerData = await portalDocument.loadViewerData();
			}

			if (!this.mounted) {
				return;
			}

			let viewports: Map<number, pdfjs.PageViewport> = new Map<number, pdfjs.PageViewport>();
			for (let pageNumber = 1; pageNumber <= viewerData.numPages; pageNumber++) {
				let page: pdfjs.PDFPageProxy = await viewerData.getPage(pageNumber);
				let viewport: pdfjs.PageViewport = page.getViewport({scale: this.state.computedZoom / 100});
				viewports.set(pageNumber, viewport);
			}

			let viewerRect: DOMRect = this.viewerElement.current.getBoundingClientRect();
			let viewerVerticalPadding: number = 0;
			if (this.props.viewerMode === ViewerMode.DEFAULT || this.props.viewerMode === ViewerMode.PORTFOLIO) {
				viewerVerticalPadding += VIEWER_PADDING;
			}

			let itemsPerRow: number = this.props.pageMode === PageViewMode.SINGLE ? 1 : 2;
			let pagesFrom: number = 1;
			let itemsInView: number = itemsPerRow;
			let viewerHeight: number = viewerVerticalPadding;

			if (!this.props.continuous) {
				pagesFrom = this.props.currentPage;
			} else {
				let computedScrollOffset: number = this.viewerElement.current.scrollTop;
				let computedViewerHeight: number = viewerRect.height - viewerVerticalPadding;

				for (let [pageNumber, viewport] of viewports.entries()) {
					let viewportHeight: number = UnitHelper.unitToPx(
						FunctionHelper.applyValue(viewport?.height, 0), LengthUnit.POINTS,
						this.props.portalResolution
					);
					let itemHeight: number = viewportHeight + PAGE_PADDING;
					let nextViewport: pdfjs.PageViewport | undefined = viewports.get(pageNumber + 1);
					let nextViewportHeight: number = UnitHelper.unitToPx(
						FunctionHelper.applyValue(nextViewport?.height, 0), LengthUnit.POINTS,
						this.props.portalResolution
					);

					if (this.props.pageMode === PageViewMode.SINGLE) {
						viewerHeight += itemHeight;

						if (computedScrollOffset - itemHeight > 0) {
							computedScrollOffset -= itemHeight;
							pagesFrom += itemsPerRow;
						} else if (computedViewerHeight - itemHeight > 0) {
							computedViewerHeight -= itemHeight;
							itemsInView += itemsPerRow;
						}
					} else {
						if (this.props.pageCover) {
							if (pageNumber === 1) {
								viewerHeight += itemHeight;

								if (computedScrollOffset - itemHeight > 0) {
									computedScrollOffset -= itemHeight;
									pagesFrom += 1;
								} else if (computedViewerHeight - itemHeight > 0) {
									computedViewerHeight -= itemHeight;
									itemsInView += 1;
								}
							} else if (pageNumber % itemsPerRow === 0) {
								if (FunctionHelper.exists(nextViewport) && nextViewportHeight > viewportHeight) {
									let nextItemHeight: number = nextViewportHeight + PAGE_PADDING;
									viewerHeight += nextItemHeight;

									if (computedScrollOffset - nextItemHeight > 0) {
										computedScrollOffset -= nextItemHeight;
										pagesFrom += itemsPerRow;
									} else if (computedViewerHeight - nextItemHeight > 0) {
										computedViewerHeight -= nextItemHeight;
										itemsInView += itemsPerRow;
									}
								} else {
									viewerHeight += itemHeight;

									if (computedScrollOffset - itemHeight > 0) {
										computedScrollOffset -= itemHeight;
										pagesFrom += itemsPerRow;
									} else if (computedViewerHeight - itemHeight > 0) {
										computedViewerHeight -= itemHeight;
										itemsInView += itemsPerRow;
									}
								}
							}
						} else if (pageNumber % itemsPerRow === 1) {
							if (FunctionHelper.exists(nextViewport) && nextViewportHeight > viewportHeight) {
								let nextItemHeight: number = nextViewportHeight + PAGE_PADDING;
								viewerHeight += nextItemHeight;

								if (computedScrollOffset - nextItemHeight > 0) {
									computedScrollOffset -= nextItemHeight;
									pagesFrom += itemsPerRow;
								} else if (computedViewerHeight - nextItemHeight > 0) {
									computedViewerHeight -= nextItemHeight;
									itemsInView += itemsPerRow;
								}
							} else {
								viewerHeight += itemHeight;

								if (computedScrollOffset - itemHeight > 0) {
									computedScrollOffset -= itemHeight;
									pagesFrom += itemsPerRow;
								} else if (computedViewerHeight - itemHeight > 0) {
									computedViewerHeight -= itemHeight;
									itemsInView += itemsPerRow;
								}
							}
						}
					}
				}
			}

			let pagesTo: number = (pagesFrom - 1) + itemsInView;
			pagesTo += itemsPerRow; // add loading offset
			pagesFrom -= itemsPerRow; // add loading offset
			pagesFrom = Math.max(pagesFrom, 1);
			pagesTo = Math.min(pagesTo, viewerData.numPages);

			let scrollOffset: number = await this.computePageOffsetY(pagesFrom);

			for (let pagenumber of this.pageVisibility.keys()) {
				if (pagenumber < pagesFrom || pagenumber > pagesTo) {
					this.pageVisibility.set(pagenumber, 0);
				}
			}

			this.setState({
				pagesFrom: pagesFrom,
				pagesTo: pagesTo,
				viewerHeight: viewerHeight,
				scrollOffset: scrollOffset
			});
		} catch (e: any) {
			// can be ignored here
		}
	}

	/**
	 * navigate to a destination on a pdf page
	 */
	public async navigateToDestination(event: DestinationEvent): Promise<void> {
		if (!FunctionHelper.exists(this.props.portalDocument?.value) || !FunctionHelper.exists(event.dest)) {
			return;
		}

		try {
			let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
				this.props.portalDocument.get().id!
			);
			let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();

			let destination: Array<any> = [];
			let ref: string = "";

			let viewerData: pdfjs.PDFDocumentProxy;
			if (this.props.viewerMode === ViewerMode.ATTACHMENT ||
				this.props.viewerMode === ViewerMode.PORTFOLIO ||
				this.props.viewerMode === ViewerMode.PORTFOLIO_PREVIEW ||
				this.props.viewerMode === ViewerMode.PORTFOLIO_PAGE) {
				let attachment: Attachment = portalDocument.getAttachment(this.props.attachmentKey!)!;
				viewerData = await portalDocument.loadViewerData(attachment);
			} else {
				viewerData = await portalDocument.loadViewerData();
			}

			if (!this.mounted) {
				return;
			}

			if (typeof event.dest === "string") {
				ref = event.dest;
				destination = FunctionHelper.applyValue(await viewerData.getDestination(event.dest), []);
			} else {
				destination = event.dest!
			}

			let xOffset: number = 0;
			let yOffset: number = 0;

			if (destination.length && destination.length > 0 && FunctionHelper.exists(destination[0].num) &&
				FunctionHelper.exists(destination[0].gen)) {
				if (destination[3] !== null) {
					yOffset = destination[3];
				}

				ref = destination[0].num + ' ' + destination[0].gen;
			}

			for (let pageNumber = 1; pageNumber <= this.state.numberOfPages; pageNumber++) {
				let pdfPage: pdfjs.PDFPageProxy = await viewerData.getPage(pageNumber);

				let pageRef: string = (pdfPage.ref as { num: number; gen: number; }).num + ' ' +
					(pdfPage.ref as { num: number; gen: number; }).gen;

				if (pageRef !== ref) {
					continue;
				}

				let scale: number = viewerSettings.activeViewer.zoomValue / 100;
				let rotation: number = PdfPageDefinition.calculateRotationSteps(pdfPage.rotate);

				if (yOffset !== 0) {
					let pageWidth: number = pdfPage.view[2];
					let pageHeight: number = pdfPage.view[3];

					switch (rotation) {
						case 0:
							xOffset = 0;
							yOffset = (pageHeight - yOffset) * scale;
							break;
						case 1:
							xOffset = (yOffset - pageWidth) * scale;
							yOffset = 0;
							break;
						case 2:
							xOffset = pageWidth * scale;
							yOffset = (yOffset - pageHeight) * scale;
							break;
						case 3:
							xOffset = (pageHeight - yOffset) * scale;
							yOffset = pageWidth * scale;
							break;
					}
				}

				xOffset += await this.computePageOffsetX(pageNumber);
				yOffset += await this.computePageOffsetY(pageNumber);

				viewerSettings.activeViewer.currentPage = pageNumber;
				this.props.context.documentManager.updateGlobalDocumentList();
				this.setScrollPosition(xOffset, yOffset);
			}
		} catch (e: any) {
			// can be ignored
		}
	}

	/**
	 * navigate to a location on a pdf page
	 */
	public async navigateToLocation(event: LocationEvent): Promise<void> {
		if (this.viewerElement.current === null) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument.get().id!
		);
		let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();

		let viewerRect: DOMRect = this.viewerElement.current.getBoundingClientRect();
		let page: PagePages | undefined = portalDocument.getPage(event.pageNumber);
		let scale: number = viewerSettings.activeViewer.zoomValue / 100;
		let rotation: number = PdfPageDefinition.calculateRotationSteps(
			FunctionHelper.applyValue(page?.rotation, PagePages.getRotationDefault())
		);

		// the start offset is the page offset
		let offsetX: number = await this.computePageOffsetX(event.pageNumber);
		let offsetY: number = await this.computePageOffsetY(event.pageNumber);

		let pageWidth: number = UnitHelper.unitToPx(
			FunctionHelper.applyValue(page?.width, PagePages.getWidthDefault()), LengthUnit.POINTS,
			this.props.portalResolution
		);
		let pageHeight: number = UnitHelper.unitToPx(
			FunctionHelper.applyValue(page?.height, PagePages.getHeightDefault()), LengthUnit.POINTS,
			this.props.portalResolution
		);
		let targetWidth: number = FunctionHelper.applyValue(event.target.width, MetadataRectangle.getWidthDefault());
		let targetHeight: number = FunctionHelper.applyValue(event.target.height, MetadataRectangle.getHeightDefault());
		let targetX: number = FunctionHelper.applyValue(event.target.x, MetadataRectangle.getXDefault());
		let targetY: number = FunctionHelper.applyValue(event.target.y, MetadataRectangle.getYDefault());

		// Add target postion and half the size to get the center
		switch (rotation) {
			case 0:
				offsetX += (targetX + (targetWidth / 2)) * scale + PAGE_PADDING;
				offsetY += (pageHeight - targetY - (targetHeight / 2)) * scale + PAGE_PADDING;
				break;
			case 1:
				offsetX += (targetY + (targetHeight / 2)) * scale + PAGE_PADDING;
				offsetY += (targetX + (targetWidth / 2)) * scale + PAGE_PADDING;
				break;
			case 2:
				offsetX += (pageWidth - targetX + (targetWidth / 2)) * scale + PAGE_PADDING;
				offsetY += (targetY + (targetHeight / 2)) * scale + PAGE_PADDING;
				break;
			case 3:
				offsetX += (pageHeight - targetY - (targetHeight / 2)) * scale + PAGE_PADDING;
				offsetY += (pageWidth - targetX + (targetWidth / 2)) * scale + PAGE_PADDING;
				break;
		}

		// move center to viewport center
		offsetX -= viewerRect.width / 2;
		offsetY -= viewerRect.height / 2;

		if (!this.props.continuous) {
			viewerSettings.activeViewer.currentPage = event.pageNumber;
			this.props.context.documentManager.updateGlobalDocumentList({}, async () => {
				await this.setScrollPosition(offsetX, offsetY);
			});
		} else {
			await this.setScrollPosition(offsetX, offsetY);
		}
	}

	private async throttledScrollChangePage(event: React.WheelEvent): Promise<void> {
		if (!FunctionHelper.exists(this.viewerElement.current)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument.get().id!
		);
		let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();

		let scrollTop: number = this.viewerElement.current!.scrollTop;
		let scrollHeight: number = this.viewerElement.current!.scrollHeight;
		let maxScroll = scrollTop + this.viewerElement.current!.offsetHeight;
		let numberOfPages: number = await portalDocument.getNumberOfPages();
		let currentPage: number = viewerSettings.activeViewer.currentPage;

		if (event.deltaY > 0 && scrollHeight < maxScroll) {
			viewerSettings.activeViewer.nextPage(numberOfPages);
			this.props.context.documentManager.updateGlobalDocumentList(undefined, () => {
				if (!FunctionHelper.exists(this.viewerElement.current)) {
					return;
				}

				if (currentPage < numberOfPages) {
					this.viewerElement.current!.scrollTop = 0;
				}
			});
		} else if (event.deltaY < 0 && scrollTop === 0) {
			viewerSettings.activeViewer.previousPage();
			this.props.context.documentManager.updateGlobalDocumentList(undefined, () => {
				if (!FunctionHelper.exists(this.viewerElement.current)) {
					return;
				}

				if (currentPage > 1) {
					this.viewerElement.current!.scrollTop = this.viewerElement.current!.scrollHeight;
				}
			});
		}
	}

	protected async setNumberOfPages(): Promise<void> {
		await super.setNumberOfPages();

		this.pageVisibility = new Map<number, number>();
		for (let pagenumber = 1; pagenumber <= this.state.numberOfPages; pagenumber++) {
			this.pageVisibility.set(pagenumber, 0);
		}
	}

	/**
	 * Sets the current scroll position of the viewer screen to the given coordinates
	 */
	private async setScrollPosition(x: number, y: number, pageNumber?: number): Promise<void> {
		if (this.viewerElement.current === null) {
			return;
		}

		let offsetX: number = 0;
		let offsetY: number = 0;

		if (FunctionHelper.exists(pageNumber)) {
			offsetX = await this.computePageOffsetX(pageNumber!);
			offsetY = await this.computePageOffsetY(pageNumber!);
		}

		this.viewerElement.current.scrollLeft = Math.max(0, offsetX + x);
		this.viewerElement.current.scrollTop = Math.max(0, offsetY + y);
	}

	/**
	 * Defers a zoom request if another one is currently queued. required for slider inputs
	 */
	private setZoom(value: number): void {
		if (!this.mounted) {
			return;
		}

		this.setState({
			currentZoomValue: value
		});

		this.throttledZoom(value);
	}

	private setDocumentErrors() {
		if (!this.mounted || !FunctionHelper.exists(this.props.portalDocument?.value) ||
			(this.props.viewerMode !== ViewerMode.DEFAULT && this.props.viewerMode !== ViewerMode.PORTFOLIO)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument.get().id!
		);
		let portalException: Exception | undefined = portalDocument.getError();

		let certificateErrors: Array<ErrorCode> = [
			ErrorCode.PDF_CERTIFICATE_KEYPAIR_FAILURE,
			ErrorCode.PDF_CERTIFICATE_PRIVATE_KEY_FAILURE,
			ErrorCode.PDF_CERTIFICATE_CERTIFICATE_FAILURE,
			ErrorCode.PDF_CERTIFICATE_KEYPAIR_MISSING
		];

		if (certificateErrors.indexOf(FunctionHelper.applyValue(portalException?.code, 0)) !== -1) {
			portalException = undefined;
		}

		this.setState({
			exception: portalException
		});
	}

	private async handlePassword(): Promise<void> {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let documentId: string = this.props.portalDocument.get().id!;
		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(documentId);

		try {
			let passwordType: PdfPassword = PdfPassword.fromJson({
				open: this.state.openPassword
			} as PdfPasswordInterface);

			portalDocument = await this.props.context.documentManager.updateSecurity(documentId, passwordType);
			if (portalDocument.getError()?.code === ErrorCode.PDF_PASSWORD_USER) {
				throw portalDocument.getError();
			}

			portalDocument.cleanViewerData();

			this.setState({
				openPassword: ""
			});
		} catch (e: any) {
			this.setState({
				exception: new Exception(Translate.value("viewerscreen.message.wrong_password", {ns: "screen"}))
			});
			return;
		}

		this.props.context.documentManager.updateGlobalDocumentList();
	}

	private async handleCertificate(): Promise<void> {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let documentId: string = this.props.portalDocument.get().id!;
		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(documentId);
		let certificate: CertificateEntry | undefined = this.state.certificate;

		try {
			let passwordType: PdfPassword = PdfPassword.fromJson({
				keyPair: {
					certificate: {
						value: certificate?.certificate,
						source: FileDataSource.Value
					}
				}
			} as PdfPasswordInterface);

			if (!certificate?.hasPrivateKey || (certificate.hasPrivateKey && !certificate.isPrivateKeyReadable)) {
				passwordType.keyPair!.privateKey = PrivateKeyFileData.fromJson({
					value: this.state.privateKey === "" ? undefined : this.state.privateKey,
					source: this.state.privateKey === "" ? undefined : FileDataSource.Value,
					password: this.state.privateKeyPassword
				} as PrivateKeyFileDataInterface);
			}

			portalDocument.cleanViewerData();
			await this.props.context.documentManager.updateSecurity(
				documentId, passwordType, false
			);

			this.setState({
				certificate: undefined,
				privateKey: "",
				privateKeyPassword: "",
			});
		} catch (exception: any) {
			let portalException: Exception = Exception.parseException(exception);

			this.setState({
				exception: portalException
			});
			return;
		}

		this.props.context.documentManager.updateGlobalDocumentList();
	}

	private handleIntersection(entries: Array<IntersectionObserverEntry>): void {
		if (!this.props.continuous && (
			this.props.viewerMode === ViewerMode.DEFAULT || this.props.viewerMode === ViewerMode.PORTFOLIO
		)) {
			return;
		}

		for (let entry of entries) {
			if (!entry.isIntersecting) {
				continue;
			}

			let pageElement: HTMLElement = entry.target as HTMLElement;
			this.pageVisibility.set(parseInt(
				FunctionHelper.applyValue(pageElement.dataset.page, "0")
			), entry.intersectionRatio);
		}

		this.throttledSetActivePage();
	}

	protected async handleMouseWheel(event: React.WheelEvent): Promise<void> {
		if (!FunctionHelper.exists(this.viewerElement.current) || (
			this.props.viewerMode !== ViewerMode.DEFAULT && this.props.viewerMode === ViewerMode.PORTFOLIO
		)) {
			return;
		}

		if (!this.props.continuous && !event.ctrlKey) {
			await this.throttledScrollChangePage(event);
		}

		if (event.ctrlKey) {
			await this.throttledMousewheelZoom(event);
		}
	}

	private async handleScroll(event: React.UIEvent<HTMLDivElement, MouseEvent>): Promise<void> {
		await this.computeViewport();

		if (this.viewerElement.current === null) {
			return;
		}

		this.setState({
			scrollTop: this.viewerElement.current.scrollTop,
			scrollLeft: this.viewerElement.current.scrollLeft
		});
	}

	private async handleResize(): Promise<void> {
		await this.updateZoom();

		if (this.viewerElement.current !== null) {
			this.setState({
				scrollWidth: this.viewerElement.current.scrollWidth,
				scrollHeight: this.viewerElement.current.scrollHeight
			});
		}
	}

	/**
	 * Handles keyboard key down events
	 */
	private async handleKeydown(event: KeyboardEvent): Promise<void> {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let fieldErrors: Map<CertificateFields, string> = this.state.fieldErrors;

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument.get().id!
		);
		let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();
		let hasPassword: boolean = portalDocument.hasPasswordProtection("open") &&
			(!portalDocument.getDocumentFile()?.password?.isOpenSet || portalDocument.hasError());
		let hasCertificate: boolean = portalDocument.hasCertificateProtection() &&
			(!portalDocument.getDocumentFile()?.password?.isCertificateSet || portalDocument.hasError());

		if (event.key.toLowerCase() === "enter") {
			if (hasPassword) {
				await this.handlePassword();
			}

			if (hasCertificate &&
				!(
					fieldErrors.size > 0 || this.state.certificate?.certificate === "" ||
					!FunctionHelper.exists(this.state.certificate?.certificate) ||
					(!this.state.certificate?.hasPrivateKey && this.state.privateKey === "")
				)
			) {
				await this.handleCertificate();
			}
		}

		if (this.props.fullscreen) {
			switch (event.key.toLowerCase()) {
				case "pageup":
				case "arrowdown":
				case "arrowleft":
					viewerSettings.activeViewer.previousPage();
					this.props.context.documentManager.updateGlobalDocumentList();
					break;
				case "pagedown":
				case "arrowup":
				case "arrowright":
					viewerSettings.activeViewer.nextPage(this.state.numberOfPages);
					this.props.context.documentManager.updateGlobalDocumentList();
					break;
				case "home":
					viewerSettings.activeViewer.firstPage();
					this.props.context.documentManager.updateGlobalDocumentList();
					break;
				case "end":
					viewerSettings.activeViewer.lastPage(this.state.numberOfPages);
					this.props.context.documentManager.updateGlobalDocumentList();
					break;
			}
		}
	}

	/**
	 * handle contextmenu events
	 * @private
	 */
	private handleContextmenu(event: Event): void {
		if (this.props.fullscreen) {
			// prevent contextmenu from opening
			event.preventDefault();
		}
	}

	/**
	 * Set the currently active page to the most visible one
	 */
	private throttledSetActivePage(): void {
		if (!FunctionHelper.exists(this.props.portalDocument?.value) || !this.props.continuous) {
			return;
		}

		let activePage: number = this.props.currentPage;
		let highestThreshold: number = FunctionHelper.applyValue(this.pageVisibility.get(activePage), 0);
		for (let [pagenumber, visibility] of this.pageVisibility.entries()) {
			if (visibility > highestThreshold) {
				activePage = pagenumber;
				highestThreshold = visibility;
			}
		}

		if (activePage !== this.props.currentPage) {
			this.stopPageScroll = true;

			let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
				this.props.portalDocument.get().id!
			);
			let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();
			viewerSettings.activeViewer.currentPage = activePage;
			this.props.context.documentManager.updateGlobalDocumentList();
		}
	}

	/**
	 * Sets a custom zoom value in the context settings
	 */
	private throttledZoom(value: number) {
		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument.get().id!
		);
		let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();
		viewerSettings.activeViewer.zoomMode = ZoomMode.CUSTOM;
		viewerSettings.activeViewer.zoomValue = value;

		this.props.context.documentManager.updateGlobalDocumentList();
	}

	private throttledUpdateZoomValue(): void {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument.get().id!
		);
		let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();

		this.setState({
			zoomValue: viewerSettings.activeViewer.zoomValue,
			currentZoomValue: viewerSettings.activeViewer.zoomValue
		});
	}

	private renderDynamicXFAMessage(): ReactNode {
		return (
			<div className={"viewer message tooltip"} ref={this.viewerElement}
				 data-pr-tooltip={
					 Translate.value("viewerscreen.message.dynxfa", {ns: "screen"})
				 }>
				{(this.props.viewerMode === ViewerMode.PREVIEW || this.props.viewerMode === ViewerMode.PORTFOLIO_PREVIEW) &&
					<>
						<div className="fallback-icon">
							<span className="icon icon-pdf warning"/>
						</div>
					</>
				}
				{this.props.viewerMode !== ViewerMode.PREVIEW && this.props.viewerMode !== ViewerMode.PORTFOLIO_PREVIEW &&
					<MessageBox type={MessageType.INFO} language={i18next.language}>
						<Translate {...this.props} data="viewerscreen.message.dynxfa" namespace="screen"/>
					</MessageBox>
				}
			</div>
		);
	}

	private renderPasswordPanel(): ReactNode {
		let error: Exception | undefined = this.state.exception;

		return (
			<div className={"viewer message"} ref={this.viewerElement}>
				{this.props.viewerMode === ViewerMode.PREVIEW &&
					<div className="fallback-icon">
						<span className="icon icon-pdf locked password"/>
					</div>
				}
				{this.props.viewerMode !== ViewerMode.PREVIEW &&
					<div className="panel">
						<div className="p-col-12">
							<MessageBox type={MessageType.INFO} closeable={false} language={i18next.language}>
								<Translate {...this.props} data="viewerscreen.message.password" namespace="screen"/>
							</MessageBox>
							{FunctionHelper.exists(error) &&
								<MessageBox {...this.props} type={MessageType.DANGER} closeable={true}
											language={i18next.language}
											onClose={() => this.setState({
												exception: undefined
											})}
											stacktrace={
												<>
													<p>{error!.message} ({error!.code})</p>
													<pre>{error!.stack}</pre>
												</>
											}>
									{error!.message}
								</MessageBox>
							}
						</div>
						<div className="p-grid">
							<div className="p-col-12 p-grid p-jc-center">
								<label className="p-mr-2" htmlFor="panel-password">
									<Translate {...this.props} data="viewerscreen.password.label" namespace="screen"/>
								</label>
								<div className="p-mr-2">
									<Password feedback={false} inputId="panel-password" toggleMask={true}
											  onChange={(e) => this.setState({
												  openPassword: e.currentTarget.value
											  })}/>
								</div>
								<Button onClick={this.handlePassword} className="primary-button">
									<Translate {...this.props} data="viewerscreen.password.button" namespace="screen"/>
								</Button>
							</div>
						</div>
					</div>
				}
			</div>
		);
	}

	private renderCertificatePanel() {
		let error: Exception | undefined = this.state.exception;
		let fieldErrors: Map<CertificateFields, string> = this.state.fieldErrors;

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument.get().id!
		);

		let serialNumbers: Array<string> = [];
		let receipients: Array<DocumentRecipient> = FunctionHelper.applyValue(
			portalDocument.getDocumentFile()?.password?.recipients, DocumentPassword.getRecipientsDefault()
		);
		for (let receipient of receipients) {
			if (!FunctionHelper.exists(receipient.serialNumber)) {
				continue;
			}

			serialNumbers.push(receipient.serialNumber!)
		}

		let certificate: x509.X509Certificate | undefined
		if (FunctionHelper.exists(this.state.certificate)) {
			try {
				certificate = new x509.X509Certificate(this.state.certificate);
			} catch (e: any) {
				// can be ignored here
			}
		}

		let userCertificates: UserCertificates = UserCertificates.fromJson(this.props.userCertificates.get());
		let certificates: Immutable<Array<CertificateEntry>> = Immutable.from(
			FunctionHelper.applyValue(userCertificates.certificates, UserCertificates.getCertificatesDefault())
		);

		return (
			<div className={"viewer message"} ref={this.viewerElement}>
				{this.props.viewerMode === ViewerMode.PREVIEW &&
					<div className="fallback-icon">
						<span className="icon icon-pdf locked certificate"/>
					</div>
				}
				{this.props.viewerMode !== ViewerMode.PREVIEW &&
					<div className="panel">
						<div className="p-col-12">
							<MessageBox type={MessageType.INFO} closeable={false} language={i18next.language}>
								<Translate {...this.props} data="viewerscreen.message.certificate" namespace="screen"/>
							</MessageBox>
							{FunctionHelper.exists(error) &&
								<MessageBox {...this.props} type={MessageType.DANGER} closeable={true}
											language={i18next.language}
											onClose={() => this.setState({
												exception: undefined
											})}
											stacktrace={
												<>
													<p>{error!.message} ({error!.code})</p>
													<pre>{error!.stack}</pre>
												</>
											}>
									{error!.message}
								</MessageBox>
							}
						</div>
						<div className="p-grid">
							<div className="p-col-12 p-grid p-jc-center">
								<label className="p-col-12 p-xl-6 label-left" htmlFor="control-certificate">
									<Translate {...this.props} data="viewerscreen.certificate.label" namespace="screen"
											   values={{
												   certificate: fieldErrors.has(CertificateFields.CERTIFICATE) ?
													   "" : FunctionHelper.applyValue(
														   certificate?.subject, ""
													   )
											   }}/>
								</label>
							</div>
							<div className="p-col-12 p-grid p-jc-center">
								<div className="p-col-12 p-xl-6">
									<CertificateInput
										{...this.props}
										className={fieldErrors.has(CertificateFields.CERTIFICATE) ? " p-invalid" : ""}
										tooltip={fieldErrors.has(CertificateFields.CERTIFICATE) ?
											Translate.value(
												fieldErrors.get(CertificateFields.CERTIFICATE)!, {ns: "message"}
											) : ""}
										type={CertificateInputType.CERTIFICATE} inputId="control-certificate"
										value={this.state.certificate?.certificate} certificates={certificates}
										onChange={(certificateEntry: CertificateEntry) => {
											if (FunctionHelper.isEmpty(certificateEntry.certificate)) {
												fieldErrors.set(CertificateFields.CERTIFICATE, "errors.fields.set");
											} else {
												try {
													let certificate: x509.X509Certificate =
														new x509.X509Certificate(certificateEntry.certificate);

													if (serialNumbers.indexOf(certificate.serialNumber) === -1) {
														fieldErrors.set(
															CertificateFields.CERTIFICATE,
															"errors.fields.certificate.serial"
														);
													} else if (certificate.notAfter < new Date()) {
														fieldErrors.set(
															CertificateFields.CERTIFICATE,
															"errors.fields.certificate.date"
														);
													} else {
														fieldErrors.delete(CertificateFields.CERTIFICATE);

														if (certificateEntry.hasPrivateKey) {
															fieldErrors.delete(CertificateFields.PRIVATE_KEY);
														}
													}
												} catch (e: any) {
													fieldErrors.set(
														CertificateFields.CERTIFICATE,
														"errors.fields.certificate.invalid"
													);
												}
											}

											this.setState({
												certificate: certificateEntry
											});
										}} serialNumbers={serialNumbers}/>
								</div>
							</div>
							{!this.state.certificate?.hasPrivateKey &&
								<>
									<div className="p-col-12 p-grid p-jc-center">
										<label className="p-col-12 p-xl-6 label-left" htmlFor="control-key">
											<Translate {...this.props} data="viewerscreen.certificate.key"
													   namespace="screen"/>
										</label>
									</div>
									<div className="p-col-12 p-grid p-jc-center">
										<div className="p-col-12 p-xl-6">
											<CertificateInput
												{...this.props}
												className={fieldErrors.has(CertificateFields.PRIVATE_KEY) ? " p-invalid" : ""}
												tooltip={fieldErrors.has(CertificateFields.PRIVATE_KEY) ?
													Translate.value(
														fieldErrors.get(CertificateFields.PRIVATE_KEY)!,
														{ns: "message"}
													) : ""}
												type={CertificateInputType.PRIVATE_KEY} inputId="control-key"
												value={this.state.privateKey}
												onChange={(certificateEntry: CertificateEntry) => {
													let privateKey: string = "";

													if (FunctionHelper.isEmpty(certificateEntry.certificate)) {
														fieldErrors.set(CertificateFields.PRIVATE_KEY, "errors.fields.set");
													} else {
														fieldErrors.delete(CertificateFields.PRIVATE_KEY);
														privateKey = certificateEntry.certificate!;
													}

													this.setState({
														privateKey: privateKey
													})
												}}/>
										</div>
									</div>
									<div className="p-col-12 p-grid p-jc-center">
										<label className="p-col-12 p-xl-6 label-left" htmlFor="control-password">
											<Translate {...this.props} data="viewerscreen.certificate.password"
													   namespace="screen"/>
										</label>
									</div>
									<div className="p-col-12 p-grid p-jc-center">
										<div className="p-col-12 p-xl-6">
											<Password feedback={false} inputId="control-password" toggleMask={true}
													  onChange={(e) => this.setState({
														  privateKeyPassword: e.currentTarget.value
													  })}/>
										</div>
									</div>
								</>
							}
							{this.state.certificate?.hasPrivateKey && !this.state.certificate?.isPrivateKeyReadable &&
								<>
									<div className="p-col-12 p-grid p-jc-center">
										<label className="p-col-12 p-xl-6 label-left" htmlFor="control-password">
											<Translate {...this.props} data="viewerscreen.certificate.password"
													   namespace="screen"/>
										</label>
									</div>
									<div className="p-col-12 p-grid p-jc-center">
										<div className="p-col-12 p-xl-6">
											<Password feedback={false} inputId="control-password" toggleMask={true}
													  onChange={(e) => this.setState({
														  privateKeyPassword: e.currentTarget.value
													  })}/>
										</div>
									</div>
								</>
							}
							{this.state.certificate?.hasPrivateKey && this.state.certificate?.isPrivateKeyReadable &&
								<>
									<div className="p-col-3 p-lg-hidden"/>
									<div className="p-col-12 p-xl-6 p-jc-center">
										<MessageBox type={MessageType.INFO} closeable={false}
													language={i18next.language}>
											<Translate {...this.props} data="viewerscreen.message.private_key_known"
													   namespace="screen"/>
										</MessageBox>
									</div>
									<div className="p-col-3 p-lg-hidden"/>
								</>
							}
							<div className="p-col-12 p-grid p-jc-center">
								<div className="p-col-12 p-xl-6">
									<Button onClick={this.handleCertificate} className="primary-button" disabled={
										fieldErrors.size > 0 || this.state.certificate?.certificate === "" ||
										!FunctionHelper.exists(this.state.certificate?.certificate) ||
										(!this.state.certificate?.hasPrivateKey && this.state.privateKey === "")
									}>
										<Translate {...this.props} data="viewerscreen.certificate.button"
												   namespace="screen"/>
									</Button>
								</div>
							</div>
						</div>
					</div>
				}
			</div>
		);
	}

	private renderErrorMessage(): ReactNode {
		if (!FunctionHelper.exists(this.state.error)) {
			return null;
		}

		let errorMessage: string = Translate.exists("errors." + this.state.error!.code, {ns: "message"}) ?
			Translate.value("errors." + this.state.error!.code, {ns: "message"}) : this.state.error!.message;

		return (
			<div className={"viewer message tooltip"} ref={this.viewerElement} data-pr-tooltip={errorMessage}>
				{this.props.viewerMode === ViewerMode.PREVIEW &&
					<>
						<div className="fallback-icon">
							<span className="icon icon-pdf"/>
						</div>
					</>
				}
				{this.props.viewerMode !== ViewerMode.PREVIEW &&
					<MessageBox type={MessageType.DANGER} closeable={false} language={i18next.language} stacktrace={
						<>
							<p>{this.state.error!.message} {FunctionHelper.exists(this.state.error!.code) &&
								<>( {this.state.error!.code} )</>
							}</p>
							<pre>{this.state.error!.stack}</pre>
						</>
					}>
						{errorMessage}
					</MessageBox>
				}
			</div>
		);
	}

	private renderNoPagesMessage(): ReactNode {
		return (
			<div className={"viewer message tooltip"} ref={this.viewerElement}
				 data-pr-tooltip={Translate.value("viewerscreen.message.no_pages", {ns: "screen"})}>
				{
					(
						this.props.viewerMode === ViewerMode.PREVIEW ||
						this.props.viewerMode === ViewerMode.ATTACHMENT ||
						this.props.viewerMode === ViewerMode.PORTFOLIO
					) &&
					<>
						<div className="fallback-icon">
							<span className="icon icon-pdf warning"/>
						</div>
					</>
				}
				{
					this.props.viewerMode !== ViewerMode.PREVIEW &&
					this.props.viewerMode !== ViewerMode.ATTACHMENT &&
					this.props.viewerMode !== ViewerMode.PORTFOLIO &&
					<MessageBox type={MessageType.INFO} language={i18next.language}>
						<Translate {...this.props} data="viewerscreen.message.no_pages" namespace="screen"/>
					</MessageBox>
				}
			</div>
		);
	}

	private renderFooter(): ReactNode {
		if ((this.props.viewerMode !== ViewerMode.DEFAULT && this.props.viewerMode !== ViewerMode.PORTFOLIO) ||
			this.props.fullscreen) {
			return;
		}

		let userSettings: UserSettings = UserSettings.fromJson(this.props.userSettings.get());

		let point: PortalOperationPoint | undefined;
		if (FunctionHelper.exists(this.props.mouseFunctionPoint)) {
			point = this.props.mouseFunctionPoint!.get();
		}

		let rect: PortalOperationRectangle | undefined;
		if (FunctionHelper.exists(this.props.mouseFunctionRect)) {
			rect = this.props.mouseFunctionRect!.get();
		}

		let line: PortalOperationLineAnnotation | undefined;
		if (FunctionHelper.exists(this.props.mouseFunctionLine)) {
			line = this.props.mouseFunctionLine!.get();
		}

		return (
			<div className="viewer-footer">
				{FunctionHelper.exists(point) &&
					<>
						<Translate {...this.props} data="viewerscreen.footer.point" namespace="screen" values={{
							x: UnitHelper.pxToUnit(
								FunctionHelper.applyValue(point!.x, Point.getXDefault()), userSettings.unit,
								this.props.portalResolution, userSettings.unit === LengthUnit.MILLIMETER ? 0 : 1
							) + " " + userSettings.unit,
							y: UnitHelper.pxToUnit(
								FunctionHelper.applyValue(point!.y, Point.getYDefault()), userSettings.unit,
								this.props.portalResolution, userSettings.unit === LengthUnit.MILLIMETER ? 0 : 1
							) + " " + userSettings.unit
						}}/>
					</>
				}
				{FunctionHelper.exists(rect) &&
					<>
						<Translate {...this.props} data="viewerscreen.footer.rect" namespace="screen" values={{
							x: UnitHelper.pxToUnit(
								FunctionHelper.applyValue(rect!.x, Rectangle.getXDefault()), userSettings.unit,
								this.props.portalResolution, userSettings.unit === LengthUnit.MILLIMETER ? 0 : 1
							) + " " + userSettings.unit,
							y: UnitHelper.pxToUnit(
								FunctionHelper.applyValue(rect!.y, Rectangle.getYDefault()), userSettings.unit,
								this.props.portalResolution, userSettings.unit === LengthUnit.MILLIMETER ? 0 : 1
							) + " " + userSettings.unit,
							width: UnitHelper.pxToUnit(
								FunctionHelper.applyValue(rect!.width, Rectangle.getWidthDefault()), userSettings.unit,
								this.props.portalResolution, userSettings.unit === LengthUnit.MILLIMETER ? 0 : 1
							) + " " + userSettings.unit,
							height: UnitHelper.pxToUnit(
								FunctionHelper.applyValue(rect!.height, Rectangle.getHeightDefault()), userSettings.unit,
								this.props.portalResolution, userSettings.unit === LengthUnit.MILLIMETER ? 0 : 1
							) + " " + userSettings.unit
						}}/>
					</>
				}
				{FunctionHelper.exists(line) &&
					<>
						<Translate {...this.props} data="viewerscreen.footer.line" namespace="screen" values={{
							xStart: UnitHelper.pxToUnit(
								FunctionHelper.applyValue(line!.start.x, Point.getXDefault()), userSettings.unit,
								this.props.portalResolution, userSettings.unit === LengthUnit.MILLIMETER ? 0 : 1
							) + " " + userSettings.unit,
							yStart: UnitHelper.pxToUnit(
								FunctionHelper.applyValue(line!.start.y, Point.getYDefault()), userSettings.unit,
								this.props.portalResolution, userSettings.unit === LengthUnit.MILLIMETER ? 0 : 1
							) + " " + userSettings.unit,
							xEnd: UnitHelper.pxToUnit(
								FunctionHelper.applyValue(line!.end.x, Point.getXDefault()), userSettings.unit,
								this.props.portalResolution, userSettings.unit === LengthUnit.MILLIMETER ? 0 : 1
							) + " " + userSettings.unit,
							yEnd: UnitHelper.pxToUnit(
								FunctionHelper.applyValue(line!.end.y, Point.getYDefault()), userSettings.unit,
								this.props.portalResolution, userSettings.unit === LengthUnit.MILLIMETER ? 0 : 1
							) + " " + userSettings.unit
						}}/>
					</>
				}
			</div>
		);
	}

	/**
	 * render ruler components for pdf documents
	 */
	private renderRuler(): ReactNode {
		if (this.props.viewerMode !== ViewerMode.DEFAULT && this.props.viewerMode !== ViewerMode.PORTFOLIO) {
			return;
		}

		let userSettings: UserSettings = UserSettings.fromJson(this.props.userSettings.get());

		return (
			<>
				<MenuButton className={"button-coordinate" + (this.state.showCoordinates ? " active" : "")}
							language={i18next.language} label="x/y"
							tooltip={
								Translate.value("viewerscreen.toolbars.coordinates", {ns: "screen"})
							}
							onClick={() => this.setState({
								showCoordinates: !this.state.showCoordinates
							})}/>
				<div className={"coordinate-panel" + (this.state.showCoordinates ? " is-visible" : "")}>
					<span>X: {
						UnitHelper.pxToUnit(
							this.state.coordinateX,
							userSettings.unit,
							this.props.portalResolution,
							userSettings.unit === LengthUnit.MILLIMETER ? 0 : 1
						)
					} {userSettings.unit}</span><br/>
					<span>Y: {
						UnitHelper.pxToUnit(
							this.state.coordinateY, userSettings.unit, this.props.portalResolution,
							userSettings.unit === LengthUnit.MILLIMETER ? 0 : 1
						)
					} {userSettings.unit}</span>
				</div>
				<MenuButton className="button-ruler-settings" icon="icon icon-cog" language={i18next.language}
							tooltip={Translate.value("viewerscreen.toolbars.unit", {ns: "screen"})}
							tooltipOptions={{position: 'left'}}
							menuItems={
								[
									{
										label: Translate.value("inputs.unit.pt"),
										icon: "icon" + (userSettings.unit === LengthUnit.POINTS ?
											" icon-check" : " empty"),
										command: () => {
											userSettings.unit = LengthUnit.POINTS;
											localStorage.setItem(StorageItem.UNIT, userSettings.unit);
											this.props.context.session.updateUserSettings(userSettings);
										}
									},
									{
										label: Translate.value("inputs.unit.pc"),
										icon: "icon" + (userSettings.unit === LengthUnit.PICA ?
											" icon-check" : " empty"),
										command: () => {
											userSettings.unit = LengthUnit.PICA;
											localStorage.setItem(StorageItem.UNIT, userSettings.unit);
											this.props.context.session.updateUserSettings(userSettings);
										}
									},
									{
										label: Translate.value("inputs.unit.mm"),
										icon: "icon" + (userSettings.unit === LengthUnit.MILLIMETER ?
											" icon-check" : " empty"),
										command: () => {
											userSettings.unit = LengthUnit.MILLIMETER;
											localStorage.setItem(StorageItem.UNIT, userSettings.unit);
											this.props.context.session.updateUserSettings(userSettings);
										}
									},
									{
										label: Translate.value("inputs.unit.in"),
										icon: "icon" + (userSettings.unit === LengthUnit.INCHES ?
											" icon-check" : " empty"),
										command: () => {
											userSettings.unit = LengthUnit.INCHES;
											localStorage.setItem(StorageItem.UNIT, userSettings.unit);
											this.props.context.session.updateUserSettings(userSettings);
										}
									}
								]
							}/>

				<Ruler {...this.props} {...this.state} direction={RulerDirection.HORIZONTAL}/>
				<Ruler {...this.props} {...this.state} direction={RulerDirection.VERTICAL}/>
			</>
		);
	}

	private renderFullscreenControls(): ReactNode {
		if (!this.props.fullscreen) {
			return null
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument.get().id!
		);
		let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();

		let viewerZoomMenu: RefObject<Menu> = createRef<Menu>();

		return (
			<>
				<div className="viewer-buttons top">
					<Button icon="icon icon-close" onClick={async () => {
						if (FunctionHelper.exists(document.exitFullscreen)) {
							try {
								await document.exitFullscreen();
							} catch (e: any) {
								// can be ignored
							}
						}
					}}/>
				</div>
				<div className="viewer-buttons bottom">
					{this.state.showFullscreenOptions &&
						<>
							<Button
								className={viewerSettings.activeViewer.zoomMode === ZoomMode.PAGE ? "active" : ""}
								icon="icon icon-zoom-fit"
								onClick={() => {
									let portalDocument: PortalDocument =
										this.props.context.documentManager.findDocument(
											this.props.portalDocument.get().id!
										);
									let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();
									viewerSettings.activeViewer.zoomMode = ZoomMode.PAGE;
									this.props.context.documentManager.updateGlobalDocumentList();
								}}/>
							<Button
								className={viewerSettings.activeViewer.zoomMode === ZoomMode.WIDTH ? "active" : ""}
								icon="icon icon-zoom-width"
								onClick={() => {
									let portalDocument: PortalDocument =
										this.props.context.documentManager.findDocument(
											this.props.portalDocument.get().id!
										);
									let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();
									viewerSettings.activeViewer.zoomMode = ZoomMode.WIDTH;
									this.props.context.documentManager.updateGlobalDocumentList();
								}}/>
							<Button
								className={viewerSettings.activeViewer.zoomMode === ZoomMode.HEIGHT ? "active" : ""}
								icon="icon icon-zoom-height"
								onClick={() => {
									let portalDocument: PortalDocument =
										this.props.context.documentManager.findDocument(
											this.props.portalDocument.get().id!
										);
									let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();
									viewerSettings.activeViewer.zoomMode = ZoomMode.HEIGHT;
									this.props.context.documentManager.updateGlobalDocumentList();
								}}/>
							<Button
								className={viewerSettings.activeViewer.zoomMode === ZoomMode.ORIGINAL ? "active" : ""}
								icon="icon icon-zoom-original"
								onClick={() => {
									let portalDocument: PortalDocument =
										this.props.context.documentManager.findDocument(
											this.props.portalDocument.get().id!
										);
									let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();
									viewerSettings.activeViewer.zoomMode = ZoomMode.ORIGINAL;
									this.props.context.documentManager.updateGlobalDocumentList();
								}}/>
							<div className="p-inputgroup zoom">
								<InputNumber max={400} min={10} value={this.state.currentZoomValue} useGrouping={false}
											 onChange={(e) => this.setZoom(
												 FunctionHelper.applyValue(e.value, 100)
											 )}/>
								<Button aria-haspopup={true} aria-controls="viewerZoomMenu" label="%"
										onClick={(event) =>
											viewerZoomMenu.current!.toggle(event)
										}/>
								<Menu popup={true} ref={viewerZoomMenu} id="viewerZoomMenu"
									  appendTo={this.viewerElement.current}
									  model={[
										  {label: "10 %", command: () => this.setZoom(10)},
										  {label: "25 %", command: () => this.setZoom(25)},
										  {label: "50 %", command: () => this.setZoom(50)},
										  {label: "75 %", command: () => this.setZoom(75)},
										  {label: "100 %", command: () => this.setZoom(100)},
										  {label: "125 %", command: () => this.setZoom(125)},
										  {label: "150 %", command: () => this.setZoom(150)},
										  {label: "200 %", command: () => this.setZoom(200)},
										  {label: "400 %", command: () => this.setZoom(400)},
									  ]}/>
							</div>
						</>
					}
					<Button icon="icon icon-more" onClick={() => {
						this.setState({
							showFullscreenOptions: !this.state.showFullscreenOptions
						});
					}}/>
				</div>
			</>
		);
	}

	private handleInteractivity(page: number) {
		if (this.state.interactivePage === page) {
			return;
		}

		this.setState({
			interactivePage: page
		});
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		if (this.state.loading) {
			return (
				<div className={"viewer message"} ref={this.viewerElement}>
					<div className="loader" ref={this.loaderElement}>
						<span className="icon icon-spinner spin"/>
					</div>
				</div>
			);
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument.get().id!
		);
		let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();

		let formType: MetadataFormsFormat | undefined = FunctionHelper.applyValue(
			portalDocument.metadata?.information?.formType, MetadataInformation.getFormTypeDefault()
		);
		if (formType === MetadataFormsFormat.DynamicXFA) {
			return this.renderDynamicXFAMessage();
		}

		let hasPassword: boolean = portalDocument.hasPasswordProtection("open") &&
			(!portalDocument.getDocumentFile()?.password?.isOpenSet || portalDocument.hasError());

		if (hasPassword) {
			return this.renderPasswordPanel();
		}

		let hasCertificate: boolean = portalDocument.hasCertificateProtection() &&
			(!portalDocument.getDocumentFile()?.password?.isCertificateSet || portalDocument.hasError());

		if (hasCertificate) {
			return this.renderCertificatePanel();
		}

		if (FunctionHelper.exists(this.state.error) && this.state.error!.code !== ErrorCode.PDF_PASSWORD_OWNER) {
			return this.renderErrorMessage();
		}

		if (this.state.numberOfPages === 0) {
			return this.renderNoPagesMessage();
		}

		if (portalDocument.isPortfolio() && (
			this.props.viewerMode === ViewerMode.DEFAULT || this.props.viewerMode === ViewerMode.PREVIEW
		)) {
			// Hide portfolio fallback page
			return (
				<div className={"viewer message"} ref={this.viewerElement}>
					<div className="fallback-icon">
						<span className="icon icon-pdf"/>
					</div>
				</div>
			);
		}

		let viewerClasses: string = "pdf-viewer";

		if (this.props.pageMode === PageViewMode.DUAL) {
			viewerClasses += " dual"
		} else {
			viewerClasses += " single"
		}

		switch (this.props.mouseMode) {
			case MouseMode.POINT:
			case MouseMode.RECTANGLE:
				viewerClasses += " area";
				break;
			case MouseMode.MOVE:
				if (this.state.moveActive) {
					viewerClasses += " moveing";
				} else {
					viewerClasses += " move";
				}
				break;
		}

		if (this.props.continuous) {
			viewerClasses += " continuous"
		}

		if (this.props.pagePadding) {
			viewerClasses += " padding"
		}

		let pageGroups: Array<ReactNode> = [];

		if (this.state.numberOfPages > 0) {
			if (this.props.viewerMode === ViewerMode.PAGE || this.props.viewerMode === ViewerMode.PORTFOLIO_PAGE) {
				pageGroups.push(
					<div className="page-group" key={this.props.currentPage}>
						<PdfPage {...this.props} pageNumber={this.props.currentPage}
								 intersectionObserver={this.intersectionObserver} zoomValue={this.state.computedZoom}
								 updateCoordinates={this.updateCoordinates} updateScroll={this.setScrollPosition}
								 handleInteractivity={() => this.handleInteractivity(this.props.currentPage)}
								 interactivePage={this.state.interactivePage}/>
					</div>
				);
			} else {
				let pdfPages: Array<ReactNode> = [];
				let pageGroupKey: string = "";

				for (let pageNumber: number = this.state.pagesFrom; pageNumber <= this.state.pagesTo; pageNumber++) {
					pdfPages.push(
						<PdfPage {...this.props} pageNumber={pageNumber} zoomValue={this.state.computedZoom}
								 intersectionObserver={this.intersectionObserver} key={pageNumber}
								 updateCoordinates={this.updateCoordinates} updateScroll={this.setScrollPosition}
								 handleInteractivity={() => this.handleInteractivity(pageNumber)}
								 interactivePage={this.state.interactivePage}/>
					);
					pageGroupKey += (pageGroupKey === "" ? "" : "|") + pageNumber;

					if ((this.props.pageCover && pageNumber % 2 === 1) ||
						(!this.props.pageCover && pageNumber % 2 === 0)) {
						pageGroups.push(
							<div className="page-group" key={pageGroupKey}>
								{pdfPages}
							</div>
						);

						pageGroupKey = "";
						pdfPages = [];
					}
				}

				if (pdfPages.length > 0) {
					pageGroups.push(
						<div className="page-group" key={pageGroupKey}>
							{pdfPages}
						</div>
					);
				}
			}
		}

		return (
			<>
				{this.renderRuler()}
				{
					FunctionHelper.applyValue(
						portalDocument.metadata?.information?.docMDP, MetadataInformation.getDocMDPDefault()
					) === DocMdp.NoChangesAllowed &&
					(this.props.viewerMode === ViewerMode.DEFAULT || this.props.viewerMode === ViewerMode.PORTFOLIO) &&
					<MessageBox type={MessageType.INFO} closeable={true} language={i18next.language}>
						<Translate {...this.props} data="viewerscreen.message.signature" namespace="screen"/>
					</MessageBox>
				}
				{viewerSettings.viewerSettings.mouseFunction === MouseFunction.FORMS_EDIT &&
					FunctionHelper.exists(viewerSettings.formInfo) && !viewerSettings.hideFormMessage &&
					(this.props.viewerMode === ViewerMode.DEFAULT || this.props.viewerMode === ViewerMode.PORTFOLIO) &&
					<MessageBox type={MessageType.INFO} closeable={true} language={i18next.language}
								onClose={() => {
									let portalDocument: PortalDocument =
										this.props.context.documentManager.findDocument(
											this.props.portalDocument.get().id!
										);
									let settings: DocumentSettings = portalDocument.getSettings();
									settings.hideFormMessage = true;
									this.props.context.documentManager.updateGlobalDocumentList();
								}}>
						<Translate {...this.props} data="viewerscreen.message.form" namespace="screen"/>
					</MessageBox>
				}
				{portalDocument.isArchive() && !viewerSettings.editArchiveConfirmation &&
					(this.props.viewerMode === ViewerMode.DEFAULT || this.props.viewerMode === ViewerMode.PORTFOLIO) &&
					<MessageBox type={MessageType.INFO} closeable={false} language={i18next.language}>
						<Translate {...this.props} data="viewerscreen.message.archive" namespace="screen"/>
						<Button label={Translate.value("viewerscreen.pdfa.button", {ns: "screen"})}
								style={{
									marginLeft: "10px"
								}}
								onClick={(event) => {
									event.stopPropagation(); // stop document selection
									let portalDocument: PortalDocument =
										this.props.context.documentManager.findDocument(
											this.props.portalDocument.get().id!
										);
									let settings: DocumentSettings = portalDocument.getSettings();
									settings.editArchiveConfirmation = true;
									this.props.context.documentManager.updateGlobalDocumentList();
								}}/>
					</MessageBox>
				}
				<div className="viewer pdf tooltip" ref={this.viewerElement} onScroll={this.handleScroll}
					 onTouchStart={this.onTouchStart} onTouchMove={this.onTouchMove} onTouchEnd={this.onTouchEnd}
					 data-pr-tooltip={
						 this.props.viewerMode === ViewerMode.PREVIEW && portalDocument.isArchive() &&
						 !viewerSettings.editArchiveConfirmation ?
							 Translate.value("viewerscreen.message.archive", {ns: "screen"}) : undefined
					 }
					 onWheel={this.handleMouseWheel}>
					<div className="loading-spacer" style={{height: this.state.viewerHeight + LengthUnit.PIXEL}}/>
					<div className={viewerClasses} style={{top: this.state.scrollOffset + LengthUnit.PIXEL}}
						 onPointerDown={
							 (event) => this.handleMouseDown(event.nativeEvent)
						 }>
						{pageGroups}
					</div>
				</div>
				{this.renderViewerControls()}
				{this.renderFullscreenControls()}
				{this.renderFooter()}
			</>
		);
	}
}

const PdfViewer = React.memo(PdfViewerDefinition,
	(prevProps: Readonly<PdfViewerProperties>, nextProps: Readonly<PdfViewerProperties>) => {
		if (!nextProps.portalDocument.equals(prevProps.portalDocument)) {
			return false;
		}

		if (!nextProps.userCertificates.equals(prevProps.userCertificates)) {
			return false;
		}

		if (FunctionHelper.exists(nextProps.textSelection) &&
			!nextProps.textSelection!.equals(prevProps.textSelection)) {
			return false;
		}

		if (FunctionHelper.exists(nextProps.textSearchResults) &&
			!nextProps.textSearchResults!.equals(prevProps.textSearchResults)) {
			return false;
		}

		if (FunctionHelper.exists(nextProps.userSettings) &&
			!nextProps.userSettings.equals(prevProps.userSettings)) {
			return false;
		}

		if (nextProps.attachmentKey !== prevProps.attachmentKey) {
			return false;
		}

		if (nextProps.pageCover !== prevProps.pageCover) {
			return false;
		}

		if (nextProps.zoomMode !== prevProps.zoomMode) {
			return false;
		}

		if (nextProps.zoomValue !== prevProps.zoomValue) {
			return false;
		}

		if (nextProps.viewerMode !== prevProps.viewerMode) {
			return false;
		}

		if (nextProps.pageMode !== prevProps.pageMode) {
			return false;
		}

		if (nextProps.mouseMode !== prevProps.mouseMode) {
			return false;
		}

		if (nextProps.continuous !== prevProps.continuous) {
			return false;
		}

		if (nextProps.pagePadding !== prevProps.pagePadding) {
			return false;
		}

		if (nextProps.currentPage !== prevProps.currentPage) {
			return false;
		}

		if (nextProps.mouseFunction !== prevProps.mouseFunction) {
			return false;
		}

		if (nextProps.fullscreen !== prevProps.fullscreen) {
			return false;
		}

		if (nextProps.textSearchSelection !== prevProps.textSearchSelection) {
			return false;
		}

		if (!prevProps.mouseFunctionAnnotationParameter?.equals(nextProps.mouseFunctionAnnotationParameter)) {
			return false;
		}

		if (nextProps.language !== prevProps.language) {
			return false;
		}

		if (nextProps.signatureEditProfile !== prevProps.signatureEditProfile) {
			return false;
		}

		if (nextProps.portalResolution !== prevProps.portalResolution) {
			return false;
		}

		if (FunctionHelper.exists(nextProps.mouseFunctionPoint) &&
			!nextProps.mouseFunctionPoint!.equals(prevProps.mouseFunctionPoint)) {
			return false;
		}

		if (FunctionHelper.exists(nextProps.mouseFunctionRect) &&
			!nextProps.mouseFunctionRect!.equals(prevProps.mouseFunctionRect)) {
			return false;
		}

		if (FunctionHelper.exists(nextProps.mouseFunctionLine) &&
			!nextProps.mouseFunctionLine!.equals(prevProps.mouseFunctionLine)) {
			return false;
		}

		if (FunctionHelper.exists(nextProps.formValues) && !nextProps.formValues!.equals(prevProps.formValues)) {
			return false;
		}

		return true;
	}
);

export default PdfViewer;